import { DirectionalHint, TooltipDelay, TooltipHost } from '@fluentui/react';
import { List } from 'immutable';
import React, { ChangeEvent, Component, createRef, KeyboardEvent, ReactNode } from 'react';
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { CaptionBolder, SmallBody, SmallBodyBolder, SmallHeading } from 'src/components/Text';
import { localize } from 'src/l10n';
import { VisageSidebarMenuItemActiveMode, VisageSidebarMode } from 'src/sidebar';
import { setSidebarMode } from 'src/sidebar/actions';
import { IVisageSidebarDomainMenu, IVisageSidebarMenuItem } from 'src/sidebar/reducer';
import { Label, setVisage2FullscreenMode, UnstyledButton } from 'src/ui';
import Visage2Icon from 'src/visage2/Visage2Icon/Visage2Icon';
import './VisageSidebar.scss';
import VisageSidebarItem from './VisageSidebarItem';
import { VisageSidebarBackButton } from './VisageSidebarBackButton';
import SpintrSearch from 'src/ui/components/SpintrList/SpintrSearch';

interface IProps {
    sidebarMode?: VisageSidebarMode,
    menuItems?: IVisageSidebarMenuItem[];
    sidebarItems?: IVisageSidebarMenuItem[];
    dispatch?: any;
    enableCrocus?: boolean;
    currentUser?: any;
    displayBackButton?: boolean;
    displaySearch?: boolean;
    domainMenu?: IVisageSidebarDomainMenu;
    isResponsive?: boolean;
    intranetName?: string;
}

// A single wrapper without a route of its own (e.g. "Nyheter") only carries the
// rows; the domain heading replaces it. The phone's "Visa undermeny" keeps it.
const getDomainRows = (items: IVisageSidebarMenuItem[]): IVisageSidebarMenuItem[] => {
    const isWrapper = items.length === 1 &&
        !items[0].route &&
        !!items[0].children;

    return isWrapper ? items[0].children : items;
}

const findMenuItemByModuleKey = (items: IVisageSidebarMenuItem[], moduleKey: string): IVisageSidebarMenuItem | undefined => {
    for (const item of items) {
        if (item.moduleKey === moduleKey) {
            return item;
        }

        if (item.children) {
            const child = findMenuItemByModuleKey(item.children, moduleKey);

            if (child) {
                return child;
            }
        }
    }

    return undefined;
}

interface IState {
    searchValue: string;
}

class VisageSidebarItems extends Component<IProps, IState> {
    private listRef = createRef<HTMLUListElement>();

    constructor(props: IProps) {
        super(props);

        this.state = {
            searchValue: ""
        }
    }

    getItems() {
        if (!this.state.searchValue) {
            return this.props.sidebarItems;
        }

        const flatList = [];

        const addItemsToFlatList = (items: IVisageSidebarMenuItem[]) => {
            for (const item of items) {
                if (item.children) {
                    addItemsToFlatList(item.children);
                } else {
                    flatList.push(item);
                }
            }
        }

        addItemsToFlatList(this.props.sidebarItems);

        const searchResult = flatList.filter((i: IVisageSidebarMenuItem) => i.name.toLowerCase().indexOf(this.state.searchValue.toLowerCase()) > -1);

        return searchResult;
    }

    getSearchHits(rootItems: IVisageSidebarMenuItem[]): IVisageSidebarMenuItem[] {
        const query = this.state.searchValue.toLowerCase();
        const hits: IVisageSidebarMenuItem[] = [];

        // All nodes with a route, also those with children, each with the path to it
        const addHits = (items: IVisageSidebarMenuItem[], path: string[]) => {
            for (const item of items) {
                if (item.isSectionLabel) {
                    continue;
                }

                const itemPath = [...path, item.name];

                if (item.route && item.name.toLowerCase().indexOf(query) > -1) {
                    hits.push({
                        ...item,
                        children: undefined,
                        lazyLoadChildren: false,
                        isExpanded: false,
                        searchPath: path.length > 0 ? itemPath.join(" › ") : localize("Huvudniva"),
                    });
                }

                if (item.children) {
                    addHits(item.children, itemPath);
                }
            }
        }

        addHits(rootItems, []);

        return hits;
    }

    getDomainTitle(): string {
        const { domainMenu, menuItems } = this.props;

        if (domainMenu.moduleKey) {
            const menuItem = findMenuItemByModuleKey(menuItems || [], domainMenu.moduleKey);

            if (menuItem && menuItem.name) {
                return menuItem.name;
            }
        }

        return domainMenu.title;
    }

    // "Alla nyheter" and the like: back to the domain's own list
    getDomainRootRows(): IVisageSidebarMenuItem[] {
        const { rootRow } = this.props.domainMenu;

        if (!rootRow) {
            return [];
        }

        return [{
            key: "domain-root",
            name: rootRow.name,
            route: rootRow.route,
            routes: [rootRow.route],
            activeMode: VisageSidebarMenuItemActiveMode.exact,
        }];
    }

    // Rows the main menu adds after the customer's own rows
    getMainMenuExtraRows(): IVisageSidebarMenuItem[] {
        const { currentUser, enableCrocus } = this.props;
        const rows: IVisageSidebarMenuItem[] = [];

        if (!currentUser.isAdmin && !currentUser.isEditor) {
            return rows;
        }

        rows.push({
            key: "admin",
            moduleKey: "admin",
            name: localize("Administration"),
            menuClassName: "sidebar-Administration-menuItem",
            icon: "setting-2",
            activeIcon: "VisageSidebarSettingsFilled",
            route: "/admin",
            routes: ["/admin"],
            activeMode: VisageSidebarMenuItemActiveMode.relative
        });

        if (enableCrocus) {
            rows.push({
                key: "marketplace",
                moduleKey: "marketplace",
                name: localize("MARKETPLACE_LIBRARY"),
                icon: "category",
                activeIcon: "VisageSidebarApps20Filled",
                route: "/marketplace/library",
                routes: ["/marketplace/library"],
                activeMode: VisageSidebarMenuItemActiveMode.relative
            });
        }

        return rows;
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if ((prevProps.displaySearch && !this.props.displaySearch) ||
            prevProps.domainMenu !== this.props.domainMenu ||
            prevProps.sidebarMode !== this.props.sidebarMode) {
            this.setState({
                searchValue: ""
            });
        }
    }

    onPanelSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Escape" && !!this.state.searchValue) {
            event.stopPropagation();

            this.setState({
                searchValue: ""
            });
        }

        // Enter searches the domain's content where it has one, otherwise opens the first hit,
        // the same way as clicking it
        if (event.key === "Enter" && !!this.state.searchValue && this.listRef.current) {
            const firstHit =
                this.listRef.current.querySelector<HTMLElement>(".VisageSidebar-menuPanel-contentSearch a") ||
                this.listRef.current.querySelector<HTMLElement>(".VisageSidebar-menuItem-wrapper > .unstyled-button");

            if (firstHit) {
                event.preventDefault();
                firstHit.click();
            }
        }
    }

    renderMenuPanel(title: string, searchPlaceholder: string, rows: IVisageSidebarMenuItem[], isMainMenu: boolean): ReactNode {
        const { searchValue } = this.state;
        const items = searchValue ?
            this.getSearchHits(rows) :
            rows;

        return (
            <nav className="VisageSidebar-menuPanel" aria-label={title}>
                {!isMainMenu && (
                    <VisageSidebarBackButton isMenuPanel onClick={() => {
                        this.props.dispatch(setSidebarMode(VisageSidebarMode.menu));
                    }} />
                )}
                <SmallHeading as="h2" className="VisageSidebar-menuPanel-heading" color="contentDark">
                    {title}
                </SmallHeading>
                <div className="VisageSidebar-menuPanel-search">
                    <SpintrSearch
                        classes="searchBox"
                        placeholder={searchPlaceholder}
                        onKeyDown={this.onPanelSearchKeyDown}
                        value={searchValue}
                        onClear={() => {
                            this.setState({
                                searchValue: ""
                            });
                        }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (!event) {
                                return;
                            }

                            const { value } = event.target;

                            this.setState({
                                searchValue: value
                            });
                        }} />
                </div>
                <ul className="sidebar-menu" ref={this.listRef}>
                    {isMainMenu && !!searchValue && (
                        // @ts-expect-error - withRouter/connect typings do not expose the own props
                        <VisageSidebarItem currentUser={this.props.currentUser} item={{
                            key: "search-hits-label",
                            name: localize("TraffarIMenyn").replace("{0}", items.length.toString()),
                            isSectionLabel: true
                        }} isChild={false} isMenuPanel />
                    )}
                    {
                        items.map((item: IVisageSidebarMenuItem, index: number) => {
                            return (
                                // @ts-expect-error - withRouter/connect typings do not expose the own props
                                <VisageSidebarItem currentUser={this.props.currentUser} item={item} isChild={false} isMenuPanel key={item.key || index} />
                            )
                        })
                    }
                    {!isMainMenu && !!searchValue && !!this.props.domainMenu?.contentSearchUrl && (
                        <li className="VisageSidebar-menuPanel-contentSearch">
                            <Link to={this.props.domainMenu.contentSearchUrl + encodeURIComponent(searchValue)}>
                                <CaptionBolder as="span">
                                    {localize("SokEfterX").replace("{0}", searchValue) + " ›"}
                                </CaptionBolder>
                            </Link>
                        </li>
                    )}
                    {!isMainMenu && !!searchValue && items.length === 0 && !this.props.domainMenu?.contentSearchUrl && (
                        <li className="VisageSidebar-menuPanel-noHits" role="status">
                            <SmallBodyBolder as="div" weight="semiBold" color="contentDark">
                                {this.props.domainMenu?.noHitsTitle || localize("IngaTraffar")}
                            </SmallBodyBolder>
                            <SmallBody as="div" color="contentNormal">
                                {localize("ProvaEttAnnatOrdEllerSokIHelaIntranatet")}
                            </SmallBody>
                        </li>
                    )}
                    {!!searchValue && (isMainMenu || items.length === 0) && (
                        <li className="VisageSidebar-menuPanel-searchAll">
                            <Link to={"/search?q=" + encodeURIComponent(searchValue)}>
                                <CaptionBolder as="span">{localize("SokIHelaIntranatet") + " ›"}</CaptionBolder>
                            </Link>
                        </li>
                    )}
                </ul>
            </nav>
        );
    }

    render() {
        if (this.props.sidebarMode === VisageSidebarMode.submenu &&
            !!this.props.domainMenu &&
            !this.props.isResponsive) {
            return this.renderMenuPanel(
                this.getDomainTitle(),
                this.props.domainMenu.searchPlaceholder,
                [...this.getDomainRootRows(), ...getDomainRows(this.props.sidebarItems)],
                false);
        }

        if (this.props.sidebarMode === VisageSidebarMode.menu &&
            !this.props.isResponsive) {
            return this.renderMenuPanel(
                this.props.intranetName,
                localize("SokIMeny"),
                [...this.props.sidebarItems, ...this.getMainMenuExtraRows()],
                true);
        }

        const items = this.getItems();

        return (
            <ul className="sidebar-menu">
                {this.props.sidebarMode === VisageSidebarMode.submenu && this.props.displaySearch && (
                    <SpintrSearch
                        classes="searchBox sidebar-top-search"
                        value={this.state.searchValue}
                        onClear={() => {
                            this.setState({
                                searchValue: ""
                            });
                        }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (!event) {
                                return;
                            }

                            const { value } = event.target;

                            this.setState({
                                searchValue: value
                            });
                        }} />
                )}
                {this.props.sidebarMode === VisageSidebarMode.submenu && this.props.displayBackButton && !this.state.searchValue && (
                    <VisageSidebarBackButton onClick={() => {
                        this.props.dispatch(setSidebarMode(VisageSidebarMode.menu));
                    }} />
                )}
                {
                    items.map((item: any, index: number) => {
                        return (
                            // @ts-ignore
                            <VisageSidebarItem currentUser={this.props.currentUser} item={item} isChild={false} key={index} />
                        )
                    })
                }
                {this.props.sidebarMode === VisageSidebarMode.menu && this.getMainMenuExtraRows().map((item: IVisageSidebarMenuItem) => (
                    // @ts-ignore
                    <VisageSidebarItem currentUser={this.props.currentUser} item={item} isChild={false} key={item.key} />
                ))}
            </ul>
        )
    }
}

const mapStateToProps = (state, props) => ({
    sidebarMode: state.sidebar.mode,
    menuItems: state.sidebar.menuItems,
    currentUser: state.profile.active,
    sidebarItems: state.sidebar.items,
    enableCrocus: state.instance.get("crocusAvailable"),
    displayBackButton: state.sidebar.displayBackButton,
    displaySearch: state.sidebar.displaySearch,
    domainMenu: state.sidebar.domainMenu,
    intranetName: state.instance.get("name")
});

export default connect(mapStateToProps)(VisageSidebarItems);